import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { __rest } from 'tslib';
import { jsx, jsxs } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { useListItem, useFloatingTree, useMergeRefs } from '@floating-ui/react';
import classnames from 'classnames';
import { Divider, Avatar, Flex, OverflowTooltip, Shortcut, Body } from '@cognite/cogs-core';
import { CheckmarkToggleIcon, ExternalLinkIcon } from '@cognite/cogs-icons';
import { useSlots, IconWrapper } from '@cognite/cogs-utils';
import { useMenuContext } from './menu-context.js';

/**
 * Menu Divider with fixed spacing
 */
var MenuDivider = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsx(Divider, Object.assign({
    spacing: "8px",
    ref: ref
  }, props));
});
// This component should be only used internally
var ListItem = /*#__PURE__*/forwardRef(function (_a, ref) {
  var children = _a.children,
    disabled = _a.disabled,
    label = _a.label,
    delegatedProps = __rest(_a, ["children", "disabled", "label"]);
  var menu = useMenuContext();
  var item = useListItem({
    label: disabled ? null : label
  });
  var tree = useFloatingTree();
  var isActive = item.index === menu.activeIndex;
  var combinedRef = useMergeRefs([ref, item.ref]);
  return jsx("li", Object.assign({
    "aria-disabled": disabled,
    ref: combinedRef,
    role: "menuitem",
    tabIndex: isActive ? 0 : -1
  }, delegatedProps, menu.getItemProps({
    onClick: function onClick(event) {
      var _a;
      if (disabled) {
        return;
      }
      // This is to disable the menu from closing when clicking inside the menu item
      if (!(menu.disableCloseOnClickInside || delegatedProps.disableCloseOnClickInside)) {
        tree === null || tree === undefined ? undefined : tree.events.emit('click');
      }
      (_a = delegatedProps.onClick) === null || _a === undefined ? undefined : _a.call(delegatedProps, event);
    },
    onFocus: function onFocus(event) {
      var _a;
      (_a = delegatedProps.onFocus) === null || _a === undefined ? undefined : _a.call(delegatedProps, event);
      menu.setHasFocusInside(true);
    }
  }), {
    children: children
  }));
});
/**
 * Menu Item with action
 */
var MenuItemAction = /*#__PURE__*/forwardRef(function (_a, ref) {
  var children = _a.children,
    disabled = _a.disabled,
    className = _a.className,
    trailingContent = _a.trailingContent,
    icon = _a.icon,
    shortcutKeys = _a.shortcutKeys,
    _a$disableTooltip = _a.disableTooltip,
    disableTooltip = _a$disableTooltip === undefined ? false : _a$disableTooltip,
    _a$variant = _a.variant,
    variant = _a$variant === undefined ? 'default' : _a$variant,
    label = _a.label,
    delegatedProps = __rest(_a, ["children", "disabled", "className", "trailingContent", "icon", "shortcutKeys", "disableTooltip", "variant", "label"]);
  var _useSlots = useSlots(children, {
      avatar: Avatar
    }),
    _useSlots2 = _slicedToArray(_useSlots, 1),
    slots = _useSlots2[0];
  var extendedClassNames = classnames('cogs-lab', 'cogs-lab-menu__item', 'cogs-lab-menu__item__action', className, {
    'cogs-lab-menu__item--variant-critical': variant === 'critical',
    'cogs-lab-menu__item--disabled': disabled,
    'cogs-lab-menu__item--with-avatar': !!slots.avatar
  });
  return jsxs(ListItem, Object.assign({
    disabled: disabled,
    label: label,
    className: extendedClassNames
  }, delegatedProps, {
    children: [!!slots.avatar && jsx("div", {
      className: "cogs-lab-menu__item__avatar",
      children: slots.avatar
    }), icon && jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      className: "cogs-lab-menu__item__icon",
      children: icon
    }), jsx("div", {
      className: "cogs-menu-item-label",
      children: jsxs(OverflowTooltip, {
        disabled: disableTooltip,
        children: [label, " "]
      })
    }), jsxs("div", {
      className: "cogs-lab cogs-lab-menu__trailing-container",
      children: [trailingContent, shortcutKeys && jsx(Shortcut, {
        className: "cogs-lab-menu__item__shortcut",
        keys: shortcutKeys
      })]
    })]
  }));
});
/**
 * Menu item for toggle options
 */
var MenuItemToggled = /*#__PURE__*/forwardRef(function (_a, ref) {
  _a.children;
    var _a$toggled = _a.toggled,
    toggled = _a$toggled === undefined ? false : _a$toggled,
    disabledLocal = _a.disabled,
    trailingContent = _a.trailingContent,
    className = _a.className,
    label = _a.label,
    shortcutKeys = _a.shortcutKeys,
    _a$disableTooltip2 = _a.disableTooltip,
    disableTooltip = _a$disableTooltip2 === undefined ? false : _a$disableTooltip2,
    delegatedProps = __rest(_a, ["children", "toggled", "disabled", "trailingContent", "className", "label", "shortcutKeys", "disableTooltip"]);
  var liClassNames = classnames('cogs-lab', 'cogs-lab-menu__item', 'cogs-lab-menu__item__selectable', {
    'cogs-lab-menu__item--toggled': toggled,
    'cogs-lab-menu__item--disabled': disabledLocal
  }, className);
  return jsxs(ListItem, Object.assign({
    ref: ref,
    disabled: disabledLocal,
    label: label,
    className: liClassNames,
    "aria-checked": toggled
  }, delegatedProps, {
    children: [jsx(Flex, {
      alignItems: "center",
      justifyContent: "center",
      className: "cogs-lab cogs-lab-menu__item__icon",
      children: toggled && jsx(CheckmarkToggleIcon, {})
    }), jsx("div", {
      className: "cogs-menu-item-label",
      children: jsxs(OverflowTooltip, {
        disabled: disableTooltip,
        children: [label, " "]
      })
    }), jsxs("div", {
      className: "cogs-lab cogs-lab-menu__trailing-container",
      children: [trailingContent, shortcutKeys && jsx(Shortcut, {
        className: "cogs-lab cogs-lab-menu__item__shortcut",
        keys: shortcutKeys
      })]
    })]
  }));
});
/**
 * Menu item for links
 */
var MenuItemLink = /*#__PURE__*/forwardRef(function (props, ref) {
  props.children;
    var _onClick = props.onClick,
    disabled = props.disabled,
    className = props.className,
    icon = props.icon,
    shortcutKeys = props.shortcutKeys,
    trailingContent = props.trailingContent,
    href = props.href,
    label = props.label,
    _props$disableTooltip = props.disableTooltip,
    disableTooltip = _props$disableTooltip === undefined ? false : _props$disableTooltip,
    delegatedProps = __rest(props, ["children", "onClick", "disabled", "className", "icon", "shortcutKeys", "trailingContent", "href", "label", "disableTooltip"]);
  var extendedClassNames = classnames('cogs-lab', 'cogs-lab-menu__item', 'cogs-lab-menu__item__link', className, {
    'cogs-lab-menu__item--disabled': disabled
  });
  return jsx(ListItem, {
    disabled: disabled,
    label: label,
    ref: ref,
    className: extendedClassNames,
    children: jsxs("a", Object.assign({
      tabIndex: -1,
      "aria-label": label,
      className: "cogs-lab cogs-lab-menu__item__link__anchor",
      href: href,
      "aria-disabled": disabled
    }, delegatedProps, {
      target: "_new",
      onClick: function onClick(event) {
        if (disabled) {
          event.preventDefault();
        } else {
          _onClick === null || _onClick === undefined ? undefined : _onClick(event);
        }
      },
      children: [icon && jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        className: "cogs-lab cogs-lab-menu__item__icon",
        children: icon
      }), jsx("div", {
        className: "cogs-menu-item-label",
        children: jsx(OverflowTooltip, {
          disabled: disableTooltip,
          children: label
        })
      }), jsx(IconWrapper, {
        size: 10,
        children: jsx(ExternalLinkIcon, {})
      }), jsxs("div", {
        className: "cogs-lab cogs-lab-menu__trailing-container",
        children: [trailingContent, shortcutKeys && jsx(Shortcut, {
          className: "cogs-lab cogs-lab-menu__item__shortcut",
          keys: shortcutKeys
        })]
      })]
    }))
  });
});
var MenuSection = /*#__PURE__*/forwardRef(function (props, ref) {
  return jsxs("div", Object.assign({
    className: "cogs-lab cogs-lab-menu-section",
    ref: ref
  }, props, {
    children: [jsx(Body, {
      className: "cogs-lab cogs-lab-menu-section__label",
      muted: true,
      size: "x-small",
      children: props.label
    }), props.children]
  }));
});

export { MenuDivider, MenuItemAction, MenuItemLink, MenuItemToggled, MenuSection };
